/**
 * DataList workflow handler registry.
 *
 * Provides a singleton registry for registering and retrieving custom
 * workflow handlers that are referenced by string key in list configurations.
 * Modules register row action handlers, bulk action handlers, custom cell
 * renderers, row click handlers, and custom workflow handlers at startup so
 * the DataList can resolve them at runtime.
 *
 * @see plan section 2.6
 */

import type * as React from 'react';

import type { ColumnConfig, DataListConfig } from '../types';

// ---------------------------------------------------------------------------
// Handler type aliases
// ---------------------------------------------------------------------------

/** Handles a per-row action (e.g. edit, delete, archive). */
export type RowActionHandlerFn = (
  row: Record<string, unknown>,
  config: DataListConfig,
) => Promise<void>;

/** Handles a bulk action on multiple selected rows. */
export type BulkActionHandlerFn = (
  rows: Record<string, unknown>[],
  config: DataListConfig,
) => Promise<void>;

/** Renders custom cell content for a column with type: 'custom'. */
export type CustomCellRendererFn = (
  value: unknown,
  row: Record<string, unknown>,
  column: ColumnConfig,
) => React.ReactNode;

/** Handles a row click event. */
export type RowClickHandlerFn = (
  row: Record<string, unknown>,
) => void;

/** Generic custom handler for lifecycle workflow hooks. */
export type CustomHandlerFn = (...args: unknown[]) => void | Promise<void>;

// ---------------------------------------------------------------------------
// Registry interface
// ---------------------------------------------------------------------------

export interface DataListWorkflowRegistry {
  /** Register a row action handler. */
  registerRowActionHandler(key: string, fn: RowActionHandlerFn): void;

  /** Get a registered row action handler. Returns `undefined` if not found. */
  getRowActionHandler(key: string): RowActionHandlerFn | undefined;

  /** Register a bulk action handler. */
  registerBulkActionHandler(key: string, fn: BulkActionHandlerFn): void;

  /** Get a registered bulk action handler. Returns `undefined` if not found. */
  getBulkActionHandler(key: string): BulkActionHandlerFn | undefined;

  /** Register a custom cell renderer. */
  registerCellRenderer(key: string, fn: CustomCellRendererFn): void;

  /** Get a registered custom cell renderer. Returns `undefined` if not found. */
  getCellRenderer(key: string): CustomCellRendererFn | undefined;

  /** Register a row click handler. */
  registerRowClickHandler(key: string, fn: RowClickHandlerFn): void;

  /** Get a registered row click handler. Returns `undefined` if not found. */
  getRowClickHandler(key: string): RowClickHandlerFn | undefined;

  /** Register a custom workflow handler (for onActionComplete.custom, etc.). */
  registerCustomHandler(key: string, fn: CustomHandlerFn): void;

  /** Get a registered custom workflow handler. Returns `undefined` if not found. */
  getCustomHandler(key: string): CustomHandlerFn | undefined;
}

// ---------------------------------------------------------------------------
// Internal storage & factory
// ---------------------------------------------------------------------------

function createDataListWorkflowRegistry(): DataListWorkflowRegistry {
  const rowActionHandlers = new Map<string, RowActionHandlerFn>();
  const bulkActionHandlers = new Map<string, BulkActionHandlerFn>();
  const cellRenderers = new Map<string, CustomCellRendererFn>();
  const rowClickHandlers = new Map<string, RowClickHandlerFn>();
  const customHandlers = new Map<string, CustomHandlerFn>();

  return {
    // -- Row action handlers -------------------------------------------------

    registerRowActionHandler(key, fn) {
      rowActionHandlers.set(key, fn);
    },

    getRowActionHandler(key) {
      return rowActionHandlers.get(key);
    },

    // -- Bulk action handlers ------------------------------------------------

    registerBulkActionHandler(key, fn) {
      bulkActionHandlers.set(key, fn);
    },

    getBulkActionHandler(key) {
      return bulkActionHandlers.get(key);
    },

    // -- Custom cell renderers -----------------------------------------------

    registerCellRenderer(key, fn) {
      cellRenderers.set(key, fn);
    },

    getCellRenderer(key) {
      return cellRenderers.get(key);
    },

    // -- Row click handlers --------------------------------------------------

    registerRowClickHandler(key, fn) {
      rowClickHandlers.set(key, fn);
    },

    getRowClickHandler(key) {
      return rowClickHandlers.get(key);
    },

    // -- Custom workflow handlers --------------------------------------------

    registerCustomHandler(key, fn) {
      customHandlers.set(key, fn);
    },

    getCustomHandler(key) {
      return customHandlers.get(key);
    },
  };
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

/**
 * Singleton DataList workflow handler registry.
 *
 * Usage:
 * ```ts
 * import { dataListWorkflowRegistry } from '@components/integrated/data-list/workflow/registry';
 *
 * dataListWorkflowRegistry.registerRowActionHandler('deleteUser', async (row, config) => {
 *   await api.delete(`/users/${row.id}`);
 * });
 *
 * dataListWorkflowRegistry.registerCellRenderer('userAvatar', (value, row) => (
 *   <Avatar src={value as string} alt={row.name as string} />
 * ));
 * ```
 */
export const dataListWorkflowRegistry: DataListWorkflowRegistry =
  createDataListWorkflowRegistry();
